import type { DashboardTileId } from './dashboardTile.constant';
import { OFFICER_DUTY_STATES, type OfficerDutyState } from './officerDutyState.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE OPERATIONS SURFACE'S BOUNDS AND ITS TILE SUBSET. [P6d]
 *
 *  ⚠️ IN `constants/` FOR `officerApp.constant.ts`'s REASON, WHICH IS MECHANICAL RATHER THAN
 *  STYLISTIC. `pwa/` reads these at RUNTIME — its date chips are built from the window the
 *  server actually applies, and its summary strip iterates the tile tuple — and §8 permits a
 *  runtime import from `@pams/shared/constants` and from nowhere else, because every module
 *  in `schema/` imports zod and this app has a 150 KB budget.
 *
 *  A second definition of a bound is how the app and the server come to disagree about what
 *  an operator's horizon is.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ SEVEN DAYS BACK AND THIRTY FORWARD, DECIDED BY THE SERVER, AND THIS IS NOT THE OFFICER
 * WINDOW.
 *
 * `APP_DUTIES_*` is an officer asking "what was I on last month" — a personal history, and
 * bounded by how many duties one person can hold. This is a nodal asking "what is happening",
 * and a CP-office scope is EVERY arrangement in the commissionerate. The past is short because
 * an operations view is about what is running rather than what ran; the future is a month
 * because that is the horizon a bandobast is planned over.
 *
 * ⚠️ IT IS A DEFAULT, NOT A CAP. A caller may send any `dateFrom`/`dateTo` inside
 * `OPS_LIST_MAX_WINDOW_DAYS`; this is only what the app asks for when it asks for nothing.
 */
export const OPS_LIST_PAST_WINDOW_DAYS = 7;
export const OPS_LIST_FUTURE_WINDOW_DAYS = 30;

/**
 * ⚠️ IT THROWS RATHER THAN NARROWING, and it is a server constant a caller cannot raise —
 * `INBOX_MAX_WINDOW_DAYS`' rule for `INBOX_MAX_WINDOW_DAYS`' reason. A silently narrowed
 * window under-reports orders, and a nodal who is not shown an arrangement does not know it
 * exists.
 *
 * ⚠️ AND IT MUST EXCEED THE DEFAULT SPAN, WHICH IS 38 DAYS INCLUSIVE. A cap below it would
 * make the no-argument request — the one the app sends on every cold start — a 422.
 * `APP_DUTIES_MAX_WINDOW_DAYS`' header records that exact trap being walked into once.
 */
export const OPS_LIST_MAX_WINDOW_DAYS = 92;

/**
 * ⚠️ 25, NOT `PAGINATION.MAX_LIMIT`'s 100. Each row carries a derived title, a place, a
 * division list and a fill state; a page is several batched reads wide, and this one is
 * rendered on a phone over Slow 4G.
 */
export const OPS_LIST_MAX_LIMIT = 25;
export const OPS_LIST_DEFAULT_LIMIT = 15;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THE SUMMARY STRIP IS A SUBSET OF [P6a]'s CATALOGUE, AND IT WRITES NO AGGREGATION.
 *
 *  `docs/dashboard/tiles.md` §7 is emphatic that a tile is a registry entry rather than a
 *  module, and the corollary here is that a SECOND surface rendering the same numbers must
 *  read the same registry. "How many lines are still unnamed" implemented twice will drift,
 *  and the two will disagree in front of the person deciding whether to ring a division.
 *
 *  ⚠️ FOUR IDS, AND EVERY ONE OF THEM HAS A SCALAR PAYLOAD. A phone strip renders ONE NUMBER
 *  per card; a ranked list, a stacked band or a top-N table does not survive 360px, and a
 *  tile whose payload is `rows[]` would have to be re-projected here — which is the second
 *  expression this file exists to avoid. `LINES_UNNAMED` leads because it is the number a
 *  nodal opens the app for.
 *
 *  ⚠️ `REQUIREMENT_FULFILMENT` IS `scopes: ['COMMISSIONERATE']` AND STAYS THAT WAY. A
 *  divisional view of "required against allocated" publishes the other divisions' shares by
 *  subtraction (`dashboardTile.constant.ts` states it), and that reasoning does not stop being
 *  true on a phone. It is in this tuple so the CP office gets it; `tileIsAvailable` drops it
 *  for a division and the strip shows three cards instead of four.
 *
 *  ⚠️ AND IT IS DELIBERATELY NOT `DASHBOARD_TILE_IDS.filter(…)`. Which four numbers a nodal
 *  wants on a phone is an editorial decision about a 360px screen, not a property derivable
 *  from a tile's declaration — a derived list would silently grow the strip the day somebody
 *  adds a scalar tile for the dashboard.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OPS_SUMMARY_TILE_IDS = [
  'LINES_UNNAMED',
  'DUTIES_UNSTAFFED',
  'ATTENDANCE',
  'REQUIREMENT_FULFILMENT',
] as const satisfies readonly DashboardTileId[];

export type OpsSummaryTileId = (typeof OPS_SUMMARY_TILE_IDS)[number];

/**
 * What a card's number MEANS, so a client renders `18` or `62%` without a second table.
 *
 * ⚠️ A UNIT RATHER THAN A PRE-FORMATTED STRING. The strip is the one surface in this system
 * that must render in Bengali the day it is asked for, and a server-formatted "62%" is a
 * string no `t()` call can reach.
 */
export const OPS_SUMMARY_UNITS = ['COUNT', 'PERCENT'] as const;
export type OpsSummaryUnit = (typeof OPS_SUMMARY_UNITS)[number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7A §5] WHAT THE OPERATIONS STATUS SELECT OFFERS — EVERY OFFICER-FACING STATE BUT
 *  `CANCELLED`, AND THE REASON IS THAT `CANCELLED` CAN NEVER MATCH THERE.
 *
 *  The operations list is `show: 'current'`, fixed (`officerOps.service.ts`), and
 *  `archiveArrangement` is the ONLY writer of a `CANCELLED` arrangement status — it stamps
 *  `archived_at` in the same save, and `restoreArrangement` reverses both. So every cancelled
 *  order is archived, and an archived order is not on this list: the option Duty Mitra offered
 *  since [P6d] answered "No arrangements found" for every nodal, every time, which reads as
 *  "nothing was cancelled" rather than "this list cannot show you that".
 *
 *  ⚠️ AN OPTION A FILTER OFFERS MUST BE ABLE TO MATCH. `officerOps.test.ts` CLAIM 5 builds an
 *  order in every member of this tuple and asserts each one is FOUND under its option — so a
 *  state added to `OFFICER_DUTY_STATES` later is offered here automatically and goes red there
 *  until somebody proves it can match. Putting `CANCELLED` back is red for the same reason.
 *
 *  ⚠️ DERIVED FROM THE TUPLE, NEVER A SECOND LIST. A hand-written four would silently miss the
 *  next state; a filter of the five cannot. The SERVER still ACCEPTS `state=CANCELLED`
 *  (`listOpsArrangementsQuerySchema` keeps `OFFICER_DUTY_STATES`): narrowing it would turn a
 *  native client's empty list into a 422, and the empty list is the true answer.
 *
 *  Here rather than in a schema for this file's own reason: `pwa/` reads it at RUN time.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type OpsStateFilter = Exclude<OfficerDutyState, 'CANCELLED'>;

export const OPS_STATE_FILTERS: readonly OpsStateFilter[] = OFFICER_DUTY_STATES.filter(
  (state): state is OpsStateFilter => state !== 'CANCELLED',
);
